import React, { useState } from 'react';
import { X, Send, CheckCircle, Phone } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Checkbox } from './ui/checkbox';
import { Link } from 'react-router-dom';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { companyInfo } from '../data/mock';
import { createLead } from '../services/api';
import { toast } from 'sonner';
import { useSettings } from '../hooks/useSettings';

const QuoteModal = ({ isOpen, onClose }) => {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    origin: '',
    destination: '',
    date: '',
    propertyType: '',
    details: '',
    consent: false,
  });

  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (!formData.name || !formData.phone || !formData.origin || !formData.destination) {
      toast.error('Por favor, preencha todos os campos obrigatórios');
      return;
    }

    if (!formData.consent) {
      toast.error('É necessário aceitar o consentimento para continuar');
      return;
    }

    setIsSubmitting(true);

    // Call Google Sheets API to create lead
    const result = await createLead(formData);

    if (result.success) {
      toast.success('Pedido enviado e salvo na planilha!');
      setIsSubmitted(true);

      // Abre o WhatsApp automaticamente após 1.5s para dar tempo do usuário ver o sucesso
      setTimeout(() => {
        handleWhatsAppClick();
      }, 1500);
    } else {
      toast.error(result.error || 'Erro ao enviar pedido. Tente novamente.');
    }

    setIsSubmitting(false);
  };

  const { settings } = useSettings();

  const handleWhatsAppClick = () => {
    const propertyTypeLabels = {
      't0': 'T0/Estúdio',
      't1': 'T1',
      't2': 'T2',
      't3': 'T3',
      't4': 'T4+',
      'comercial': 'Comercial/Escritório',
      'armazem': 'Armazém'
    };

    const detailsText = formData.details ? `\n*Detalhes:* ${formData.details}` : '';
    const dateText = formData.date ? `\n*Data:* ${formData.date}` : '';
    const emailText = formData.email ? `\n*E-mail:* ${formData.email}` : '';
    const propertyTypeText = formData.propertyType ? `\n*Tipo de Imóvel:* ${propertyTypeLabels[formData.propertyType] || formData.propertyType}` : '';

    const message = encodeURIComponent(
      `*Novo Pedido de Orçamento*\n\n` +
      `*Nome:* ${formData.name}\n` +
      `*Telefone:* ${formData.phone}${emailText}\n` +
      `*Origem:* ${formData.origin}\n` +
      `*Destino:* ${formData.destination}${dateText}${propertyTypeText}${detailsText}\n\n` +
      `Olá, aguardo o seu contacto para o orçamento. Obrigado!`
    );
    window.open(`https://wa.me/${settings.whatsappPhone}?text=${message}`, '_blank');
  };

  const handleCloseModal = () => {
    setIsSubmitted(false);
    setFormData({
      name: '',
      phone: '',
      email: '',
      origin: '',
      destination: '',
      date: '',
      propertyType: '',
      details: '',
      consent: false,
    });
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleCloseModal}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        {!isSubmitted ? (
          <>
            <DialogHeader>
              <DialogTitle className="text-2xl font-bold text-[#071028]">
                Pedido de Orçamento Gratuito
              </DialogTitle>
              <DialogDescription className="text-gray-600">
                Preencha o formulário e receba resposta em até 1 hora durante o horário comercial.
              </DialogDescription>
            </DialogHeader>

            {/* Trust Bar - Redução de Fricção */}
            <div className="bg-gradient-to-r from-[#1FA2FF]/10 to-[#0077C8]/10 border border-[#1FA2FF]/20 rounded-lg p-4 -mt-2">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
                <div className="flex items-center gap-2 text-gray-700">
                  <span className="text-[#1FA2FF] font-bold">🔒</span>
                  <span className="font-medium">Dados 100% seguros e privados</span>
                </div>
                <div className="flex items-center gap-2 text-gray-700">
                  <span className="text-green-600 font-bold">✓</span>
                  <span>Não compartilhamos com terceiros</span>
                </div>
                <div className="flex items-center gap-2 text-gray-700">
                  <span className="text-green-600 font-bold">✓</span>
                  <span>Sem spam - apenas 1 contacto</span>
                </div>
                <div className="flex items-center gap-2 text-gray-700">
                  <span className="text-[#1FA2FF] font-bold">⏱️</span>
                  <span className="font-medium">Resposta garantida em 1 hora</span>
                </div>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid sm:grid-cols-2 gap-4">
                {/* Name */}
                <div className="space-y-2">
                  <Label htmlFor="name" className="text-[#071028] font-semibold">
                    Nome Completo <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="Ex: João Silva"
                    required
                    className="border-gray-300 focus:border-[#1FA2FF] focus:ring-[#1FA2FF]"
                  />
                </div>

                {/* Phone */}
                <div className="space-y-2">
                  <Label htmlFor="phone" className="text-[#071028] font-semibold">
                    Telefone/WhatsApp <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="phone"
                    name="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="+351 XXX XXX XXX"
                    required
                    className="border-gray-300 focus:border-[#1FA2FF] focus:ring-[#1FA2FF]"
                  />
                </div>
              </div>

              {/* Email */}
              <div className="space-y-2">
                <Label htmlFor="email" className="text-[#071028] font-semibold">
                  E-mail <span className="text-gray-500 text-sm">(Opcional)</span>
                </Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="exemplo@email.com"
                  className="border-gray-300 focus:border-[#1FA2FF] focus:ring-[#1FA2FF]"
                />
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                {/* Origin */}
                <div className="space-y-2">
                  <Label htmlFor="origin" className="text-[#071028] font-semibold">
                    Origem (Cidade/Rua) <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="origin"
                    name="origin"
                    value={formData.origin}
                    onChange={handleChange}
                    placeholder="Ex: Lisboa, Rua X"
                    required
                    className="border-gray-300 focus:border-[#1FA2FF] focus:ring-[#1FA2FF]"
                  />
                </div>

                {/* Destination */}
                <div className="space-y-2">
                  <Label htmlFor="destination" className="text-[#071028] font-semibold">
                    Destino (Cidade/Rua) <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="destination"
                    name="destination"
                    value={formData.destination}
                    onChange={handleChange}
                    placeholder="Ex: Cascais, Rua Y"
                    required
                    className="border-gray-300 focus:border-[#1FA2FF] focus:ring-[#1FA2FF]"
                  />
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                {/* Date */}
                <div className="space-y-2">
                  <Label htmlFor="date" className="text-[#071028] font-semibold">
                    Data Aproximada
                  </Label>
                  <Input
                    id="date"
                    name="date"
                    type="date"
                    value={formData.date}
                    onChange={handleChange}
                    className="border-gray-300 focus:border-[#1FA2FF] focus:ring-[#1FA2FF]"
                  />
                </div>

                {/* Property Type */}
                <div className="space-y-2">
                  <Label htmlFor="propertyType" className="text-[#071028] font-semibold">
                    Tipo de Imóvel
                  </Label>
                  <Select
                    value={formData.propertyType}
                    onValueChange={(value) =>
                      setFormData(prev => ({ ...prev, propertyType: value }))
                    }
                  >
                    <SelectTrigger className="border-gray-300 focus:border-[#1FA2FF] focus:ring-[#1FA2FF]">
                      <SelectValue placeholder="Selecione" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="t0">T0/Estúdio</SelectItem>
                      <SelectItem value="t1">T1</SelectItem>
                      <SelectItem value="t2">T2</SelectItem>
                      <SelectItem value="t3">T3</SelectItem>
                      <SelectItem value="t4">T4+</SelectItem>
                      <SelectItem value="comercial">Comercial/Escritório</SelectItem>
                      <SelectItem value="armazem">Armazém</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Details */}
              <div className="space-y-2">
                <Label htmlFor="details" className="text-[#071028] font-semibold">
                  Detalhes Adicionais
                </Label>
                <Textarea
                  id="details"
                  name="details"
                  value={formData.details}
                  onChange={handleChange}
                  placeholder="Ex: Móveis grandes, objetos frágeis, elevador disponível..."
                  rows={4}
                  className="border-gray-300 focus:border-[#1FA2FF] focus:ring-[#1FA2FF] resize-none"
                />
              </div>

              {/* Consent Checkbox */}
              <div className="flex items-start gap-3 p-4 bg-gray-50 rounded-lg">
                <Checkbox
                  id="consent"
                  checked={formData.consent}
                  onCheckedChange={(checked) =>
                    setFormData(prev => ({ ...prev, consent: checked }))
                  }
                  className="mt-1"
                />
                <Label htmlFor="consent" className="text-sm text-gray-700 leading-relaxed cursor-pointer">
                  Autorizo o contacto por WhatsApp/Telemóvel para fins de orçamento e
                  concordo com a{' '}
                  <Link to="/privacidade" className="text-[#1FA2FF] hover:underline">
                    Política de Privacidade
                  </Link>
                  .
                </Label>
              </div>

              {/* Trust Elements Before Submit */}
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-center">
                <p className="text-sm text-gray-700 font-medium mb-2">
                  ✓ Orçamento 100% Gratuito e Sem Compromisso
                </p>
                <p className="text-xs text-gray-600">
                  Receba o seu orçamento personalizado sem qualquer custo ou obrigação de contratar
                </p>
              </div>

              {/* Submit Button */}
              <Button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-[#1FA2FF] hover:bg-[#0077C8] text-white font-bold text-lg py-6 rounded-lg transition-all duration-300 hover:scale-105 hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                    A enviar...
                  </>
                ) : (
                  <>
                    <Send className="w-5 h-5 mr-2" />
                    Receber Orçamento Grátis Agora
                  </>
                )}
              </Button>

              {/* Final Reassurance */}
              <p className="text-xs text-center text-gray-500 -mt-2">
                🔒 Conexão segura • Resposta em até 1 hora • Cancelamento gratuito
              </p>
            </form>
          </>
        ) : (
          <div className="py-8 text-center space-y-6">
            <div className="flex justify-center">
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center">
                <CheckCircle className="w-12 h-12 text-green-500" />
              </div>
            </div>

            <div>
              <h3 className="text-2xl font-bold text-[#071028] mb-3">
                Pedido Enviado com Sucesso!
              </h3>
              <p className="text-gray-600 leading-relaxed">
                Recebemos o seu pedido. Em breve entraremos em contacto por WhatsApp
                para confirmar detalhes e enviar o orçamento.
              </p>
            </div>

            <div className="bg-[#F3F5F8] rounded-xl p-6">
              <p className="text-sm text-gray-700 mb-4">
                Enquanto espera, pode contactar-nos diretamente:
              </p>
              <Button
                onClick={handleWhatsAppClick}
                className="w-full bg-[#1FA2FF] hover:bg-[#0077C8] text-white font-semibold py-3"
              >
                <Phone className="w-5 h-5 mr-2" />
                Abrir WhatsApp
              </Button>
            </div>

            <Button
              onClick={handleCloseModal}
              variant="outline"
              className="w-full border-gray-300 text-gray-700 hover:bg-gray-50"
            >
              Fechar
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default QuoteModal;
